import { useNavigate } from 'react-router';
import { Badge, InlineError, Skeleton } from '../../components/ui';
import { OUTCOME_LABELS, OUTCOME_TONES, PIPELINE_LABELS } from '../deals/types';
import { dealJumpHref } from './leadDealLinks';
import type { DealLink } from './leadDealLinks';

/**
 * The deals behind a lead or a company, as rows that open the deal.
 *
 * One component for both panels on purpose: the lead's Deals section and the
 * company's Deals tab must read identically, because they are the same
 * question asked from two directions. See leadDealLinks.ts for why the rows
 * exist at all.
 *
 * Reuses the entity-chip row the company drawer already uses for its leads,
 * so the two lists inside one drawer do not invent two row styles.
 */
export function DealLinkList({
  deals,
  isPending,
  isError,
  emptyText,
}: {
  deals: DealLink[] | undefined;
  isPending: boolean;
  isError: boolean;
  /** What no-deals means HERE — the lead and company wordings differ. */
  emptyText: string;
}) {
  const navigate = useNavigate();

  if (isPending) {
    return <Skeleton lines={1} label="Loading deals" />;
  }
  if (isError) {
    // The deals module gate refusing is the likeliest cause, and "error" would
    // send people hunting a bug that is actually a permission.
    return (
      <InlineError>Deals could not be loaded. They may be limited to the sales team.</InlineError>
    );
  }
  const rows = deals ?? [];
  if (rows.length === 0) {
    return <p className="tracker-field-note">{emptyText}</p>;
  }
  return (
    <div className="entity-chips">
      {rows.map((deal) => (
        <button
          key={deal.id}
          type="button"
          className="entity-chip"
          title={`Open ${deal.name} in Deals`}
          onClick={() => {
            void navigate(dealJumpHref(deal));
          }}
        >
          <span className="entity-chip-name">{deal.name}</span>
          <span className="entity-chip-meta">
            {deal.stage_name === null
              ? PIPELINE_LABELS[deal.pipeline]
              : `${PIPELINE_LABELS[deal.pipeline]} · ${deal.stage_name}`}
          </span>
          <Badge tone={OUTCOME_TONES[deal.outcome]} micro>
            {OUTCOME_LABELS[deal.outcome]}
          </Badge>
        </button>
      ))}
    </div>
  );
}
